import {
  Box,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import firebase, { firestore } from '../../../../utils/firebase/firebase'
import { useEffect, useState } from 'react'
import Loader from '../../../../components/Loader'

const GuestPass = () => {
  const params = useParams()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const passes = user && user.guestPass

  useEffect(() => {
    const unsubscribe = firestore
      .collection('users')
      .doc(params.uid)
      .onSnapshot(
        (doc) => {
          if (!doc.exists) {
            setLoading(false)
            toast.error('User not found')
          } else {
            setUser({ ...doc.data(), id: doc.id })
            setLoading(false)
          }
        },
        (error) => {
          toast.error('Error retrieving user: ', error.message)
          setLoading(false)
        }
      )

    return () => {
      unsubscribe()
    }
  }, [params.uid])

  // Count guest passes used in current calendar month
  const getMonthlyGuestPassCount = () => {
    if (!passes || passes.length === 0) return 0

    const now = new Date()
    const currentMonth = now.getMonth() // 0-11
    const currentYear = now.getFullYear()

    return passes.filter((timestamp) => {
      try {
        // Parse UK locale format: "DD/MM/YYYY, HH:MM:SS"
        const [datePart] = timestamp.split(', ')
        const [day, month, year] = datePart.split('/')

        const passDate = new Date(year, parseInt(month) - 1, day)
        return (
          passDate.getMonth() === currentMonth &&
          passDate.getFullYear() === currentYear
        )
      } catch (error) {
        console.error('Error parsing guest pass timestamp:', timestamp)
        return false
      }
    }).length
  }

  // Group guest passes by month for accordion view
  const getGroupedPasses = () => {
    if (!passes || passes.length === 0) return []

    const grouped = {}

    passes.forEach((timestamp) => {
      try {
        // Parse UK locale format: "DD/MM/YYYY, HH:MM:SS"
        const [datePart, timePart] = timestamp.split(', ')
        const [day, month, year] = datePart.split('/')

        const passDate = new Date(year, parseInt(month) - 1, day)
        const monthKey = `${passDate.getFullYear()}-${String(
          passDate.getMonth() + 1
        ).padStart(2, '0')}`
        const monthLabel = passDate.toLocaleDateString('en-GB', {
          month: 'long',
          year: 'numeric',
        })

        if (!grouped[monthKey]) {
          grouped[monthKey] = {
            label: monthLabel,
            passes: [],
            sortDate: passDate,
          }
        }

        grouped[monthKey].passes.push({
          timestamp,
          date: passDate,
          formattedDate: passDate.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          }),
          formattedTime: timePart,
        })
      } catch (error) {
        console.error('Error parsing guest pass timestamp:', timestamp)
      }
    })

    // Convert to array and sort by most recent first
    return Object.entries(grouped)
      .map(([key, value]) => ({
        key,
        ...value,
      }))
      .sort((a, b) => b.sortDate - a.sortDate)
      .map((group) => ({
        ...group,
        passes: group.passes.sort((a, b) => b.date - a.date), // Sort passes within month
      }))
  }

  const registerGuest = () => {
    const currentCredits = user.credits

    // Check credit balance first
    if (currentCredits < 8) {
      return toast.error('Not enough credits')
    }

    // Check monthly limit
    const monthlyCount = getMonthlyGuestPassCount()
    if (monthlyCount >= 2) {
      // Show confirmation dialog for override
      setConfirmDialogOpen(true)
      return
    }

    // Under limit - proceed with check-in
    processCheckIn()
  }

  const processCheckIn = () => {
    var userRef = firestore.collection('users').doc(params.uid)
    const now = new Date().toLocaleString('en-GB')
    const currentCredits = user.credits

    try {
      if (user.guestPass) {
        userRef.update({
          guestPass: firebase.firestore.FieldValue.arrayUnion(now),
        })

        userRef.update({ credits: currentCredits - 8 })

        toast.success(
          `Credits reduced from ${currentCredits} to ${currentCredits - 8} `
        )
      } else {
        userRef.set({ guestPass: [now] }, { merge: true })
        userRef.update({ credits: currentCredits - 8 })
        toast.success(
          `Credits reduced from ${currentCredits} to ${currentCredits - 8} `
        )
      }
    } catch (error) {
      toast.error('unable to checking')
    }
  }

  const handleConfirmOverride = () => {
    setConfirmDialogOpen(false)
    processCheckIn()
  }

  const handleCancelOverride = () => {
    setConfirmDialogOpen(false)
  }

  if (loading) {
    return <Loader />
  }

  const monthlyCount = getMonthlyGuestPassCount()
  const chipColor =
    monthlyCount === 0 ? 'success' : monthlyCount === 1 ? 'success' : monthlyCount === 2 ? 'warning' : 'error'

  return (
    <Stack>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'start'}
      >
        <Box mb={2}>
          <Typography variant='h5' color='white.main'>
            Guest Pass
          </Typography>
          <Typography fontSize={'small'}>
            Check in this member's guest and credits are automatically deducted
            from their account.
          </Typography>
        </Box>
        <Button variant='outlined' onClick={() => registerGuest()}>
          Check in
        </Button>
      </Box>
      <Box mb={2}>
        <Chip
          label={`Guest Passes Used This Month: ${monthlyCount}/2`}
          color={chipColor}
          size='small'
        />
      </Box>
      <Typography variant='h6' color='white.main'>
        History
      </Typography>
      <Typography fontSize={'small'} mb={2}>
        A history of all guest passes used by this member, grouped by month.
      </Typography>
      <Box>
        {passes && passes.length > 0 ? (
          getGroupedPasses().map((monthGroup) => {
            const isCurrentMonth =
              monthGroup.key ===
              `${new Date().getFullYear()}-${String(
                new Date().getMonth() + 1
              ).padStart(2, '0')}`

            return (
              <Accordion
                key={monthGroup.key}
                defaultExpanded={isCurrentMonth}
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: isCurrentMonth
                      ? 'rgba(46, 125, 50, 0.1)'
                      : 'transparent',
                  }}
                >
                  <Typography>
                    {monthGroup.label}{' '}
                    <Chip
                      label={`${monthGroup.passes.length} ${
                        monthGroup.passes.length === 1 ? 'pass' : 'passes'
                      }`}
                      size='small'
                      color={isCurrentMonth ? 'success' : 'default'}
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {monthGroup.passes.map((pass, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${pass.formattedDate} at ${pass.formattedTime}`}
                          primaryTypographyProps={{ fontSize: 'small' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )
          })
        ) : (
          <Typography fontSize={'small'} color='text.secondary'>
            No guest passes used yet.
          </Typography>
        )}{' '}
      </Box>

      {/* Confirmation Dialog for Override */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelOverride}
        aria-labelledby='confirm-dialog-title'
        aria-describedby='confirm-dialog-description'
      >
        <DialogTitle id='confirm-dialog-title'>Monthly Limit Reached</DialogTitle>
        <DialogContent>
          <DialogContentText id='confirm-dialog-description'>
            This member has already used {monthlyCount} guest pass
            {monthlyCount !== 1 ? 'es' : ''} this month (limit: 2 per month).
            <br />
            <br />
            Do you want to proceed anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOverride} color='primary'>
            Cancel
          </Button>
          <Button onClick={handleConfirmOverride} color='warning' variant='contained'>
            Yes, Check In Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default GuestPass
